import React from 'react';
import { getFeatureFlagValue } from '@citrite/feature-flags';
import { colorPalette, sizes } from '@sharefiledev/antd-config';
import {
	dataTablesExtensionSlots,
	dcPiletCapabilities,
	piletOverlays,
	TableEntityType,
} from '@sharefiledev/dc-pilet-sdk';
import { projectsExtensionSlots } from '@sharefiledev/projects-pilet-sdk';
import type { PiletApi } from '@sharefiledev/sharefile-appshell';
import { Flex } from 'antd';
import dayjs from 'dayjs';
import localeData from 'dayjs/plugin/localeData';
import utc from 'dayjs/plugin/utc';
import weekday from 'dayjs/plugin/weekday';
import { getTable, getTableRecord } from './api/dcsApi';
import { addBlocks } from './Blocks';
import { getCapabilities } from './capabilities/capabilities';
import { AsyncLazyBlockDevelopment } from './Development';
import { FeatureFlag } from './featureFlags';
import { DataTable } from './layouts/table';
import AsyncLazyApp from './LazyApp';
import { StyledExportModal } from './Main.styled';
import {
	AsyncLazyClientViewDrawer,
	AsyncLazyCreateTableModal,
	AsyncLazyDataTableListUnresolved,
	AsyncLazyDeleteTableModal,
	AsyncLazyEditTableModal,
	AsyncLazyExportTableModal,
	AsyncLazyRecordViewDrawer,
} from './overlays';
import { t } from './utils';
import { Icons } from './utils/icons/icon';
import { setLogger } from './utils/logger';
import { TranslatedString } from './utils/TranslatedString';
import { RidDetails } from './utils/types';
import { ridParser } from './utils/utils';

export const setup = (app: PiletApi) => {
	dayjs.extend(weekday);
	dayjs.extend(localeData);
	dayjs.extend(utc);

	setLogger(app.sf.getLogger());

	registerApp(app);

	app.registerExtension(projectsExtensionSlots.tabs, ({ params }) => (
		<AsyncLazyApp piletApi={app} params={params} />
	));

	// data tables tab in Projects.
	app.registerExtension(dataTablesExtensionSlots.projectTab, ({ params }) => (
		<AsyncLazyApp piletApi={app} params={params} />
	));

	app.registerExtension(
		dataTablesExtensionSlots.unresolvedTasksCount,
		({ params }) =>
			(<AsyncLazyDataTableListUnresolved piletApi={app} params={params} />) as any
	);

	const drawerPropsRenderer = {
		drawerProps: {
			title: t('dynamic-components:form.title.update'),
			width: '700px',
			footer: null,
		},
	};

	const isProjectsSolutionEnabled = getFeatureFlagValue(
		FeatureFlag.EnableProjectAsSolution
	);

	// service-provider drawer
	app.registerDrawer(
		piletOverlays.viewRequestRecordViewDrawer,
		({ options, cancelInteraction, completeInteraction }) => (
			<AsyncLazyRecordViewDrawer
				piletApi={app}
				completeInteraction={completeInteraction}
				cancelInteraction={cancelInteraction}
				containerRID={options?.containerRID || ''}
				isProjectFrozen={options?.readonly ?? false}
				originAnalyticsTrackingId={options?.originAnalyticsTrackingId || ''}
			/>
		),
		{
			...drawerPropsRenderer,
		}
	);

	// client-view drawer
	app.registerDrawer(
		piletOverlays.clinetViewRequestRecordViewDrawer,
		({ options, cancelInteraction, completeInteraction }) => (
			<AsyncLazyClientViewDrawer
				piletApi={app}
				completeInteraction={completeInteraction}
				cancelInteraction={cancelInteraction}
				containerRID={options?.params?.containerRID || ''}
				originAnalyticsTrackingId={options?.params?.originAnalyticsTrackingId || ''}
			/>
		),
		{
			...drawerPropsRenderer,
		}
	);

	// create-table modal
	app.registerModal(
		piletOverlays.createTableModal,
		props => <AsyncLazyCreateTableModal {...props} />,
		{
			modalProps: {
				title: (
					<ModalTitle>
						<TranslatedString localizationKey="dynamic-components:addTab.createTable" />
					</ModalTitle>
				),
				centered: true,
				width: 500,
				footer: null,
				styles: {
					body: {
						height: '230px',
					},
				},
			},
		}
	);

	// edit-table modal
	app.registerModal(
		piletOverlays.editTableModal,
		props => <AsyncLazyEditTableModal {...props} />,
		{
			modalProps: {
				title: (
					<ModalTitle>
						<TranslatedString localizationKey="dynamic-components:editTab.modalTitle" />
					</ModalTitle>
				),
				centered: true,
				width: 500,
				footer: null,
				styles: {
					body: {
						height: '120px',
					},
				},
			},
		}
	);

	app.registerModal(
		piletOverlays.exportTableModal,
		props => <AsyncLazyExportTableModal {...props} />,
		{
			modalProps: {
				centered: true,
				width: 500,
				footer: null,
				modalRender: modalContent => (
					<StyledExportModal>{modalContent as React.ReactNode}</StyledExportModal>
				),
			},
		}
	);

	// delete-table modal
	app.registerModal(
		piletOverlays.deleteTableModal,
		props => <AsyncLazyDeleteTableModal {...props} />,
		{
			modalProps: {
				title: (
					<Flex gap={8}>
						<Icons.WarningCircleFilled color={colorPalette.red6} />
						<TranslatedString localizationKey="dynamic-components:deleteModal.title" />
					</Flex>
				),
				centered: true,
				width: 400,
				footer: null,
				closeIcon: false,
			},
		}
	);

	const capabilities = getCapabilities(app);
	app.sf.registerCapability(
		dcPiletCapabilities.tableCapabilities,
		capabilities[TableEntityType.table]
	);
	app.sf.registerCapability(
		dcPiletCapabilities.recordCapabilities,
		capabilities[TableEntityType.record]
	);

	// Register URL rewrites for table and record resources
	app.sf.registerViewEngineResourceURLRewrite({
		resourceType: 'table',
		defaultPath: isProjectsSolutionEnabled
			? '/view/projects/details?projectRID={projectRID}&activeTab={tableRID}&tab=table-{tableId}'
			: '/projects/{projectId}/table-{tableId}?activeTab={tableRID}',
		clientPath: '/#/',
		callback: async (rid: string) => {
			const tableRID = ridParser(rid, TableEntityType.table) as string;
			const table = await getTable(tableRID);

			const ridDetails = ridParser(rid, undefined, true) as RidDetails;
			return {
				tableRID: table._id,
				projectRID: table.container_id,
				accountId: ridDetails.accountId,
				projectId: ridDetails.projectId,
				tableId: ridDetails.tableId,
			};
		},
	});

	// Register URL rewrites for table and record resources
	app.sf.registerViewEngineResourceURLRewrite({
		resourceType: 'dcr',
		defaultPath: isProjectsSolutionEnabled
			? '/view/projects/details?projectRID={projectRID}&activeTab={tableRID}&tab=table-{tableId}&record=dcr-{recordId}&viewRecord=true'
			: '/projects/{projectId}/table-{tableId}?activeTab={tableRID}&record=dcr-{recordId}&viewRecord=true',
		clientPath: '?taskRID={recordRID}&taskType=datatable&taskName={title}',
		callback: async (rid: string) => {
			const recordRID = ridParser(rid, TableEntityType.record) as string;
			const record = await getTableRecord(recordRID);

			const tableRID = ridParser(rid, TableEntityType.table) as string;
			const table = await getTable(tableRID);

			const ridDetails = ridParser(rid, undefined, true) as RidDetails;

			return {
				recordRID: record._id,
				title: record.title,
				tableRID: table._id,
				projectRID: table.container_id,
				accountId: ridDetails.accountId,
				projectId: ridDetails.projectId,
				tableId: ridDetails.tableId,
				recordId: ridDetails.recordId,
			};
		},
	});

	if (process.env.NODE_ENV === 'development') {
		app.sf.registerLeftNavComponent({
			href: '/dynamic-tables-dev',
			title: () => 'Test Local',
			icon: () => null,
		});

		app.registerPage('/dynamic-tables-dev', () => (
			<AsyncLazyBlockDevelopment piletApi={app} />
		));
	}
};

const registerApp = (app: PiletApi) => {
	addBlocks(app);

	// extension slot for data-table library
	app.registerExtension(dataTablesExtensionSlots.dataTable, ({ params }) => (
		<DataTable {...params} />
	));
};

const ModalTitle: React.FC<{ children: React.ReactNode }> = ({ children }) => (
	<span style={{ position: 'relative', bottom: sizes.base }}>{children}</span>
);
